'use client';

import React, { useState, useEffect } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import { Clock, Calendar, AlertOctagon, Sun, Moon } from 'lucide-react';
import { getRahuKaalCalculatorApi } from '@/lib/api/client';
import { formatLocalizedDate, formatLocalizedTime } from '@/lib/i18n/dateFormat';

export interface RahuKaalCalculatorClientProps {
  initialData?: any;
  initialError?: string | null;
}

export function RahuKaalCalculatorClient({
  initialData = null,
  initialError = null
}: RahuKaalCalculatorClientProps) {
  const t = useTranslations('rahuKaal');
  const locale = useLocale();

  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [data, setData] = useState<any>(initialData);

  const fetchRahuKaal = async (selectedDate: string) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getRahuKaalCalculatorApi(selectedDate);
      setData(res);
    } catch (err: any) {
      setError(err.message || t('calculationFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!initialData) {
      fetchRahuKaal(date);
    }
  }, []);

  const formatDisplayTime = (timeStr: string) => {
    if (!timeStr) return '';
    return formatLocalizedTime(timeStr, locale);
  };

  const formatDisplayDate = (dateStr: string) => {
    if (!dateStr) return '';
    return formatLocalizedDate(dateStr, locale);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8" data-testid="rahu-kaal-client">
      {/* Header Banner */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 text-center space-y-3 shadow-xs">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--line)] bg-[var(--card-warm)] text-xs font-mono text-[var(--gold)]">
          <Clock className="w-3.5 h-3.5" />
          <span>{t('eyebrow')}</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-serif font-semibold tracking-tight text-[var(--foreground)]">
          {t('title')}
        </h1>
        <p className="text-xs sm:text-sm text-[var(--muted)] max-w-xl mx-auto leading-relaxed">
          {t('subtitle')}
        </p>
      </div>

      {/* Date Selector Form */}
      <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 space-y-4">
        <div className="flex flex-col sm:flex-row items-center gap-4">
          <div className="w-full sm:w-1/2 space-y-1">
            <label className="text-xs font-mono text-[var(--muted)] flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-[var(--gold)]" />
              <span>{t('selectDate')}</span>
            </label>
            <input
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full bg-[var(--background)] border border-[var(--line)] rounded-xl px-4 py-2.5 text-xs text-[var(--foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--ring)]"
            />
          </div>
          <button
            onClick={() => fetchRahuKaal(date)}
            disabled={loading}
            className="w-full sm:w-auto mt-auto px-6 py-2.5 rounded-xl bg-[var(--gold)] text-[var(--ink)] font-bold text-xs hover:bg-[var(--gold-soft)] disabled:opacity-50 transition-colors cursor-pointer"
          >
            {t('calculateBtn')}
          </button>
        </div>

        {loading && (
          <div className="text-center py-4 text-xs font-mono text-[rgba(220,236,255,0.7)] animate-pulse">
            {t('calculating')}
          </div>
        )}

        {error && (
          <div data-testid="rahu-kaal-error" className="p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-500 text-xs font-mono text-center">
            {error}
          </div>
        )}
      </div>

      {/* Result Window Display */}
      {data && (
        <div className="bg-[var(--card)] border border-[var(--line)] rounded-2xl p-6 sm:p-8 space-y-6 shadow-md" data-testid="rahu-kaal-result">
          <div className="text-center space-y-2">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-rose-500/10 text-rose-500 text-xs font-bold font-mono border border-rose-500/30">
              <AlertOctagon className="w-4 h-4" />
              <span>{t('inauspiciousWindow')}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-serif font-bold text-[var(--gold)] pt-2">
              {formatDisplayTime(data.rahu_kaal?.display)}
            </h2>
            <p className="text-xs text-[var(--muted)]">
              {t('dateLocationLabel', {
                date: formatDisplayDate(data.date),
                location: '28.61° N, 77.20° E'
              })}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-[var(--line)]">
            <div className="p-4 rounded-xl bg-[var(--background)] border border-[var(--line)] text-center space-y-1">
              <Sun className="w-5 h-5 text-[var(--gold)] mx-auto" />
              <span className="text-[10px] font-mono text-[var(--muted)] uppercase block">{t('sunrise')}</span>
              <span className="text-sm font-semibold font-mono text-[var(--foreground)]">{formatDisplayTime(data.sunrise)}</span>
            </div>
            <div className="p-4 rounded-xl bg-[var(--background)] border border-[var(--line)] text-center space-y-1">
              <Moon className="w-5 h-5 text-[var(--gold)] mx-auto" />
              <span className="text-[10px] font-mono text-[var(--muted)] uppercase block">{t('sunset')}</span>
              <span className="text-sm font-semibold font-mono text-[var(--foreground)]">{formatDisplayTime(data.sunset)}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
